import { useState, useRef, useEffect } from "react";
import * as Y from "yjs";
import { invoke } from "../lib/bridge";
import { useYDoc } from "../hooks/useYDoc";
import { useAutoResize } from "../hooks/useAutoResize";
import { DocMeta } from "../App";
import { DecryptedAvatar } from "./DecryptedAvatar";
import { linkify } from "./linkify";
import { Spinner } from "./ui/Spinner";

interface ListItemViewProps {
  doc: DocMeta;
  itemId: string;
  syncVersion?: number;
  onBack: () => void;
}

export function ListItemView({ doc, itemId, syncVersion, onBack }: ListItemViewProps) {
  const { doc: yDoc, loading, error } = useYDoc(doc.docId, syncVersion);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seeded, setSeeded] = useState(false);

  // Locate the live Y.Map for this item (mirrors ListView's lookup-by-id loop).
  const findItem = (): Y.Map<unknown> | null => {
    if (!yDoc) return null;
    const yItems = yDoc.getArray("items");
    for (let i = 0; i < yItems.length; i++) {
      const yItem = yItems.get(i) as Y.Map<unknown>;
      if (yItem.get("id") === itemId) return yItem;
    }
    return null;
  };

  // Seed the editable fields once, after the doc has loaded.
  useEffect(() => {
    if (!yDoc || seeded) return;
    const item = findItem();
    if (item) {
      setName((item.get("name") as string) ?? "");
      setDescription((item.get("description") as string) ?? "");
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yDoc, seeded]);

  // Auto-grow the description textarea to fit its content.
  useAutoResize(textareaRef, [description, editingDesc]);

  if (loading) return <div className="container"><Spinner /></div>;
  if (error) return <div className="container"><p>Error: {error}</p></div>;
  if (!yDoc) return null;

  const setField = (key: string, value: unknown) => {
    const item = findItem();
    if (!item) return;
    yDoc.transact(() => item.set(key, value));
  };

  const clearField = (key: string) => {
    const item = findItem();
    if (!item) return;
    yDoc.transact(() => item.delete(key));
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setField("name", value);
  };

  const handleDescChange = (value: string) => {
    setDescription(value);
    setField("description", value);
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await invoke<{ photoUrl: string; thumbUrl: string }>("upload_photo", { file });
      setField("photoUrl", result.thumbUrl);
    } catch (err) {
      console.error("Failed to upload photo:", err);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const item = findItem();
  if (!item) {
    return (
      <div className="container" style={{ padding: "16px" }}>
        <div className="header-row">
          <h1 className="header-title">Item</h1>
          <button onClick={onBack} className="btn-close" aria-label="Close">&times;</button>
        </div>
        <p style={{ color: "var(--color-muted)" }}>This item was deleted.</p>
      </div>
    );
  }

  const photoUrl = (item.get("photoUrl") as string | undefined) ?? null;

  return (
    <div className="container" style={{ padding: "16px" }}>
      <div className="header-row">
        <h1 className="header-title">Item</h1>
        <button onClick={onBack} className="btn-close" aria-label="Close">&times;</button>
      </div>

      <div className="member-detail">
        <div className="avatar-section">
          <div style={{ position: "relative" }}>
            <DecryptedAvatar token={photoUrl} name={name} size="large" onClick={() => fileInputRef.current?.click()} />
            {uploading && <div className="avatar-uploading">...</div>}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelected}
            style={{ display: "none" }}
          />
          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="btn-secondary btn-small">
              {uploading ? "Uploading..." : photoUrl ? "Change Photo" : "Add Photo"}
            </button>
            {photoUrl && (
              <button onClick={() => clearField("photoUrl")} disabled={uploading} className="btn-secondary btn-small">
                Delete Photo
              </button>
            )}
          </div>
        </div>

        <div className="member-detail-row">
          <span className="field-label">Name</span>
          <input
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Item name"
            className="field-input"
          />
        </div>

        <div className="member-detail-row">
          <span className="field-label">Description</span>
          {editingDesc ? (
            <div className="note-editor-scroll">
              <textarea
                ref={textareaRef}
                value={description}
                onChange={(e) => handleDescChange(e.target.value)}
                onBlur={() => setEditingDesc(false)}
                placeholder="Add a description, a link, notes..."
                className="note-editor"
              />
            </div>
          ) : (
            <div
              className="meal-description"
              onClick={() => setEditingDesc(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setEditingDesc(true);
                }
              }}
              role="button"
              tabIndex={0}
            >
              {description
                ? linkify(description)
                : <span style={{ color: "var(--color-muted)" }}>Add a description, a link, notes...</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
